import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { userService } from '../../services/user.service'
import './InputPermissionsUser.scss'

interface InputPermissionsUserProps {
  value?: number[]
  onChange?: (value: number[]) => void
  disabled?: boolean
  /** If provided, use these instead of loading (e.g. when parent already loaded users) */
  users?: Array<{ id: number; username: string }>
  menuPortalZIndex?: number
}

export const InputPermissionsUser: React.FC<InputPermissionsUserProps> = ({
  value = [],
  onChange,
  disabled = false,
  users: usersProp,
  menuPortalZIndex = 1060,
}) => {
  const { t } = useTranslation()
  const [usersState, setUsersState] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const users = usersProp ?? usersState

  useEffect(() => {
    if (usersProp != null) return
    const loadUsers = async () => {
      if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)) {
        setUsersState([])
        return
      }
      setLoading(true)
      try {
        const response = await userService.listAll()
        setUsersState(response.results || [])
      } catch (error) {
        // 403 etc - silently fail, api.service shows toast
      } finally {
        setLoading(false)
      }
    }
    loadUsers().catch(() => {}) // Safety net - prevent unhandled rejection
  }, [usersProp])

  const handleChange = (selectedOptions: any) => {
    const selectedUsers = selectedOptions ? selectedOptions.map((option: any) => option.value) : []
    onChange?.(selectedUsers)
  }

  const options = (() => {
    const mapped = users.map((user) => ({ value: user.id, label: user.username }))
    const existingIds = new Set(mapped.map((opt) => opt.value))
    value.forEach((id) => {
      if (!existingIds.has(id)) {
        mapped.push({ value: id, label: `${t('user')} #${id}` })
      }
    })
    return mapped
  })()

  const getValue = () => {
    const selectedSet = new Set(value)
    return options.filter((opt) => selectedSet.has(opt.value))
  }

  const customStyles = {
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: '#6c757d',
      color: '#ffffff',
      borderRadius: '0.375rem',
      padding: '0.25rem 0.5rem',
      margin: '0.125rem',
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: '#ffffff',
      fontSize: '0.875rem',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: '#ffffff',
      ':hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        color: '#ffffff',
      },
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: menuPortalZIndex,
    }),
  }

  return (
    <div className="docdot-input-select" style={{ opacity: disabled ? 0.6 : 1 }}>
      <div>
        <Select
          isMulti
          options={options}
          value={getValue()}
          onChange={handleChange}
          placeholder={t('select_users')}
          className="react-select-container"
          classNamePrefix="react-select"
          isClearable={true}
          isSearchable={false}
          openMenuOnClick={true}
          isDisabled={disabled}
          isLoading={loading}
          styles={customStyles}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          filterOption={(option, inputValue) => {
            return option.label.toLowerCase().includes(inputValue.toLowerCase())
          }}
          components={{
            MultiValue: ({ data, removeProps }) => {
              return (
                <div
                  className="tag-wrap btn p-0 d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: '#343a40',
                    color: '#ffffff',
                    borderRadius: '0.375rem',
                    padding: '0.375rem 0.75rem',
                    margin: '0.125rem',
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (removeProps.onClick) {
                        removeProps.onClick(e as any)
                      }
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="btn p-0 d-flex align-items-center"
                    style={{
                      color: '#ffffff',
                      padding: '0',
                      margin: '0 5px',
                      gap: '0.25rem',
                    }}
                    title={t('remove_user')}
                  >
                    <i className="bi bi-x" style={{ fontSize: '1.125rem', display: 'flex', alignItems: 'center' }}></i>
                    <span style={{ fontSize: '0.875rem', padding: '0.125rem 0' }}>{data.label}</span>
                  </button>
                </div>
              )
            },
            ClearIndicator: ({ innerProps }) => (
              <div {...innerProps} className="react-select__clear-indicator" title={t('clear_all')}>
                <i className="bi bi-x" style={{ fontSize: '1rem' }}></i>
              </div>
            ),
          }}
        />
      </div>
    </div>
  )
}
